"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Icon from "@/components/Icon";
import Logo from "@/components/Logo";
import { EXTRA_SECTIONS, SECTIONS, SETTINGS_SECTIONS, Section } from "@/lib/sections";
import { BadgeInfo, badgesFromSummary } from "@/lib/badges";

export default function Nav() {
  const pathname = usePathname();
  const [badges, setBadges] = useState<Record<string, BadgeInfo>>({});

  // refresh counts whenever the page changes
  useEffect(() => {
    fetch("/api/summary")
      .then((r) => r.json())
      .then((s) => setBadges(badgesFromSummary(s)))
      .catch(() => {});
  }, [pathname]);

  // Light / Dark for the pages (the sidebar stays the same); the choice is remembered on this browser
  const [theme, setTheme] = useState<"light" | "dark">("light");
  useEffect(() => {
    // the saved choice is only readable in the browser, so it's picked up after the first render
    if (document.documentElement.getAttribute("data-theme") === "dark") queueMicrotask(() => setTheme("dark"));
  }, []);
  function chooseTheme(t: "light" | "dark") {
    setTheme(t);
    try {
      if (t === "dark") localStorage.setItem("khw-theme", "dark");
      else localStorage.removeItem("khw-theme");
    } catch {}
    const root = document.documentElement;
    if (t === "dark") root.setAttribute("data-theme", "dark");
    else root.removeAttribute("data-theme");
  }

  async function logOut() {
    await fetch("/api/auth", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "logout" }),
    });
    window.location.href = "/login";
  }

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`);

  const link = (s: Section) => {
    const active = isActive(s.href);
    const badge = s.badge ? badges[s.badge] : undefined;
    return (
      <Link
        key={s.href}
        href={s.href}
        className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
          active ? "bg-on-brand text-brand font-medium" : "text-on-brand/90 hover:bg-brand-dark"
        }`}
      >
        <Icon name={s.icon} className="w-4 h-4 shrink-0" />
        <span className="flex-1">{s.label}</span>
        {badge && badge.count > 0 && (
          <span
            className={`text-[11px] min-w-5 px-1.5 rounded-full text-center ${
              badge.urgent ? "bg-danger text-white" : active ? "bg-brand text-on-brand" : "bg-sidebar-pink text-brand-dark"
            }`}
          >
            {badge.count}
          </span>
        )}
      </Link>
    );
  };

  if (pathname === "/login") return null;

  // The aside carries the red background for the full page height; the nav inside stays pinned.
  return (
    <aside className="bg-brand text-on-brand md:w-60 shrink-0">
    <nav className="md:sticky md:top-0 md:h-screen flex md:flex-col p-3 md:p-5 gap-2 md:gap-1 overflow-x-auto md:overflow-y-auto">
      <Link href="/" className="md:mb-5 px-2 shrink-0 flex items-center gap-3" aria-label="KH Workshop home">
        <Logo tone="light" className="h-8 md:h-12 w-auto shrink-0" />
        <div className="md:border-l md:border-on-brand/20 md:pl-3 whitespace-nowrap">
          <div className="text-lg tracking-wide leading-tight hidden md:block">Workshop</div>
          <div className="text-xs text-sidebar-pink hidden md:block">Command Center</div>
        </div>
      </Link>
      <div className="flex md:flex-col gap-1 md:flex-1">
        {link({ href: "/", label: "Dashboard", icon: "home", blurb: "" })}
        {SECTIONS.map(link)}
        <div className="hidden md:block border-t border-on-brand/15 my-2" />
        {SETTINGS_SECTIONS.map(link)}
        {/* side projects sit quietly at the bottom, above Light / Dark */}
        <div className="md:mt-auto flex md:flex-col gap-1">
          {EXTRA_SECTIONS.map((s) => (
            <Link
              key={s.href}
              href={s.href}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors ${
                isActive(s.href) ? "bg-on-brand text-brand font-medium" : "text-on-brand/60 hover:text-on-brand hover:bg-brand-dark"
              }`}
            >
              <Icon name={s.icon} className="w-3.5 h-3.5 shrink-0" />
              {s.label}
            </Link>
          ))}
        </div>
        <div
          role="radiogroup"
          aria-label="Appearance"
          className="shrink-0 flex rounded-md border border-on-brand/20 p-0.5 text-[11px] self-start md:self-stretch"
        >
          {(["light", "dark"] as const).map((t) => (
            <button
              key={t}
              role="radio"
              aria-checked={theme === t}
              onClick={() => chooseTheme(t)}
              className={`flex-1 px-2 py-1 rounded capitalize whitespace-nowrap ${
                theme === t ? "bg-on-brand/15 text-on-brand" : "text-on-brand/60 hover:text-on-brand"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <button
          onClick={logOut}
          className="shrink-0 px-3 py-2 rounded-md text-left text-sm text-on-brand/60 hover:text-on-brand hover:bg-white/5 whitespace-nowrap"
        >
          Log out
        </button>
      </div>
    </nav>
    </aside>
  );
}
